import React, { useState, useMemo, useRef, useEffect } from 'react';
import { usePDF } from 'react-to-pdf';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import Stopwatch from './Stopwatch';
import { 
  Plus, Minus, ArrowUpDown, FileDown, Trash2, ArrowLeft, Swords, Home, MapPin, 
  Notebook, Save, Eraser, Check, Bold, Italic, Underline, List, ListOrdered, Eye, Edit3,
  X
} from 'lucide-react';

function GameStats({ game, onUpdateStats, onUpdateNotes, onDeleteGame, players, onBack }) {
  const stats = useMemo(() => ['goals', 'assists'], []);
  const { toPDF, targetRef } = usePDF({ filename: `${game.name}_stats.pdf` });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [opponentScore, setOpponentScore] = useState(game.opponent_score || 0);
  const [notes, setNotes] = useState(game.notes || '');
  const [savedStatus, setSavedStatus] = useState('');
  const [activeTab, setActiveTab] = useState('edit'); // 'edit' or 'preview'
  const [selectedPlayerIdForModal, setSelectedPlayerIdForModal] = useState(null);
  const textareaRef = useRef(null);

  const handleDeleteGame = () => {
    if (window.confirm(`Are you sure you want to delete the game "${game.name}"? This action cannot be undone.`)) {
      onDeleteGame(game.id);
      if (onBack) onBack();
    }
  };

  const handleSaveNotes = async () => {
    if (onUpdateNotes) {
      await onUpdateNotes(game.id, notes);
      setSavedStatus('Saved!');
      setTimeout(() => setSavedStatus(''), 2500);
    }
  };

  const handleClearNotes = async () => {
    if (notes.trim() && window.confirm("Are you sure you want to clear the coach's notes for this game?")) {
      setNotes('');
      if (onUpdateNotes) {
        await onUpdateNotes(game.id, '');
        setSavedStatus('Cleared!');
        setTimeout(() => setSavedStatus(''), 2500);
      }
    }
  };

  // Formatting Toolbar Handlers
  const insertFormatting = (syntaxStart, syntaxEnd = syntaxStart, defaultText = 'text') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = notes.substring(start, end) || defaultText;

    const before = notes.substring(0, start);
    const after = notes.substring(end);

    const newText = `${before}${syntaxStart}${selected}${syntaxEnd}${after}`;
    setNotes(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + syntaxStart.length,
        start + syntaxStart.length + selected.length
      );
    }, 0);
  };

  const insertList = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = notes.substring(start, end);

    const before = notes.substring(0, start);
    const after = notes.substring(end);

    let listText = '';
    if (selected.trim()) {
      const lines = selected.split('\n');
      listText = lines
        .map((line, idx) => (type === 'bullet' ? `- ${line}` : `${idx + 1}. ${line}`))
        .join('\n');
    } else {
      listText = type === 'bullet' ? '- Item 1\n- Item 2' : '1. Item 1\n2. Item 2';
    }

    const prefix = before.endsWith('\n') || before.length === 0 ? '' : '\n';
    const newText = `${before}${prefix}${listText}${after}`;
    setNotes(newText);

    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const renderFormattedNotes = (rawText) => {
    if (!rawText || !rawText.trim()) {
      return <p className="text-muted-foreground italic text-sm py-4 text-center">No notes entered yet. Use the Write tab to add match feedback!</p>;
    }

    const lines = rawText.split('\n');
    return lines.map((line, idx) => {
      let formatted = line;
      // Bold
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Italic
      formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');

      // Bullet List item
      if (/^\s*-\s+/.test(line)) {
        const content = formatted.replace(/^\s*-\s+/, '');
        return (
          <li key={idx} className="ml-5 list-disc text-sm py-0.5" dangerouslySetInnerHTML={{ __html: content }} />
        );
      }

      // Numbered List item
      if (/^\s*\d+\.\s+/.test(line)) {
        const content = formatted.replace(/^\s*\d+\.\s+/, '');
        return (
          <li key={idx} className="ml-5 list-decimal text-sm py-0.5" dangerouslySetInnerHTML={{ __html: content }} />
        );
      }

      if (!line.trim()) {
        return <div key={idx} className="h-2" />;
      }

      return (
        <p key={idx} className="text-sm py-0.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />
      );
    });
  };

  const { sortedPlayerStats, totals } = useMemo(() => {
    const playerStats = game.playerStats || [];
    let sortableItems = [...playerStats];

    if (sortConfig.key === 'playerOrder' && players) {
      sortableItems.sort((a, b) => {
        const aIndex = players.findIndex(player => player.id === a.player_id);
        const bIndex = players.findIndex(player => player.id === b.player_id);
        return aIndex - bIndex;
      });
    } else if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (sortConfig.key === 'name') {
          if (a.player?.name < b.player?.name) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (a.player?.name > b.player?.name) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        } else {
          if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        }
      });
    }

    const totals = sortableItems.reduce((acc, playerStat) => {
      stats.forEach(stat => {
        acc[stat] = (acc[stat] || 0) + (playerStat[stat] || 0);
      });
      return acc;
    }, {});

    return { sortedPlayerStats: sortableItems, totals };
  }, [game.playerStats, sortConfig, stats, players]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleOpponentScoreChange = (change) => {
    const newScore = Math.max(0, opponentScore + change);
    setOpponentScore(newScore);
    onUpdateStats(game.id, null, 'opponent_score', newScore);
  };

  // Currently selected player for stat popup modal
  const activeModalPlayer = useMemo(() => {
    if (!selectedPlayerIdForModal) return null;
    return (game.playerStats || []).find(ps => ps.player_id === selectedPlayerIdForModal) || null;
  }, [selectedPlayerIdForModal, game.playerStats]);

  // Close modal when pressing Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedPlayerIdForModal(null);
      }
    };
    if (selectedPlayerIdForModal) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedPlayerIdForModal]);

  const handleRecordStat = (playerId, stat) => {
    const playerStat = (game.playerStats || []).find(ps => ps.player_id === playerId);
    const currentVal = playerStat ? (playerStat[stat] || 0) : 0;
    onUpdateStats(game.id, playerId, stat, currentVal + 1);
    setSelectedPlayerIdForModal(null);
  };

  const handleSubtractStat = (playerId, stat) => {
    const playerStat = (game.playerStats || []).find(ps => ps.player_id === playerId);
    const currentVal = playerStat ? (playerStat[stat] || 0) : 0;
    onUpdateStats(game.id, playerId, stat, Math.max(0, currentVal - 1));
    setSelectedPlayerIdForModal(null);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const teamScore = totals.goals || 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-28 w-full min-w-0">
      {/* Navigation Top Bar */}
      {onBack && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="flex items-center gap-1.5 font-medium hover:bg-accent"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Badge variant="outline" className="text-xs font-mono">
            GAME DETAIL VIEW
          </Badge>
        </div>
      )}

      {/* 1. Header Card: Game Name, Location, Scoreboard, Download PDF & Delete */}
      <Card ref={targetRef} className="border shadow-md w-full min-w-0 overflow-hidden">
        <CardHeader className="px-3 sm:px-6 pt-4 sm:pt-6 pb-4 border-b">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="w-full md:w-auto min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <CardTitle className="text-2xl sm:text-3xl font-extrabold tracking-tight break-words">{game.name}</CardTitle>
                {game.location && (
                  <Badge variant="outline" className="text-xs font-normal flex items-center gap-1 shrink-0">
                    {game.location === 'Home' ? <Home className="w-3 h-3 text-blue-500" /> : <MapPin className="w-3 h-3 text-amber-500" />}
                    {game.location}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-stretch md:self-auto justify-end shrink-0">
              <Button variant="outline" size="sm" onClick={toPDF} className="flex items-center gap-1.5">
                <FileDown className="w-4 h-4 text-emerald-500" />
                Download
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleDeleteGame}
                title="Delete Game"
                className="h-9 w-9 hover:bg-destructive/10 border-input"
              >
                <Trash2 className="w-4 h-4 text-red-500 hover:text-red-600" />
              </Button>
            </div>
          </div>

          {/* Live Scoreboard Header - Default Base Text Colors */}
          <div className="mt-4 p-3 sm:p-4 rounded-xl bg-muted/40 border flex flex-col sm:flex-row items-center justify-between gap-4 w-full min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Swords className="w-4 h-4 text-primary" />
              <span>Live Match Scoreboard</span>
            </div>

            <div className="flex items-center gap-6 sm:gap-8">
              {/* Team Score */}
              <div className="text-center">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground font-semibold">Team Score</span>
                <span className="text-3xl font-extrabold text-foreground">{teamScore}</span>
              </div>

              <span className="text-2xl font-bold text-muted-foreground">VS</span>

              {/* Opponent Score */}
              <div className="text-center">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground font-semibold">Opponent Score</span>
                <span className="text-3xl font-extrabold text-foreground">{opponentScore}</span>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-3 sm:px-6 pt-4 space-y-5 w-full min-w-0">
          {/* Dedicated Opponent Score Controls Row */}
          <div className="p-3 sm:p-4 bg-muted/30 rounded-xl border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 w-full min-w-0">
            <div className="w-full sm:w-auto min-w-0">
              <span className="font-bold text-base sm:text-sm text-foreground break-words block">
                {game.name}
              </span>
            </div>
            <div className="w-full sm:w-auto shrink-0 flex items-center rounded-lg border border-input shadow-sm bg-background overflow-hidden">
              <Button
                type="button"
                variant="ghost"
                className="flex-1 sm:flex-none h-11 sm:h-8 sm:w-10 rounded-none border-r border-input hover:bg-accent text-muted-foreground hover:text-foreground p-0 flex items-center justify-center"
                onClick={() => handleOpponentScoreChange(-1)}
                title="Subtract Opponent Goal"
              >
                <Minus className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </Button>
              <span className="w-16 sm:w-12 font-extrabold text-lg sm:text-base text-center px-2 select-none shrink-0">
                {opponentScore}
              </span>
              <Button
                type="button"
                variant="ghost"
                className="flex-1 sm:flex-none h-11 sm:h-8 sm:w-10 rounded-none border-l border-input hover:bg-accent text-foreground p-0 flex items-center justify-center"
                onClick={() => handleOpponentScoreChange(1)}
                title="Add Opponent Goal"
              >
                <Plus className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </Button>
            </div>
          </div>

          {/* 2. Player Listing Section with Goals / Assists Inputs */}
          <div className="space-y-4 pt-2 w-full min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3 px-1 sm:px-0 w-full min-w-0">
              <h3 className="font-bold text-lg flex items-center gap-2">
                Player Stats Tracking
              </h3>

              {/* Sort Options */}
              <div className="flex flex-wrap items-center gap-1.5 text-xs w-full sm:w-auto">
                <span className="font-semibold text-muted-foreground flex items-center gap-1 mr-1">
                  <ArrowUpDown className="w-3.5 h-3.5" /> Sort:
                </span>
                <Button
                  size="sm"
                  variant={sortConfig.key === 'playerOrder' ? 'default' : 'outline'}
                  onClick={() => requestSort('playerOrder')}
                  className="h-7 text-xs px-2.5"
                >
                  Default
                </Button>
                <Button
                  size="sm"
                  variant={sortConfig.key === 'goals' ? 'default' : 'outline'}
                  onClick={() => requestSort('goals')}
                  className="h-7 text-xs px-2.5"
                >
                  Goals {sortConfig.key === 'goals' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                </Button>
              </div>
            </div>

            {/* Grid View */}
            {sortedPlayerStats.length === 0 ? (
              <div className="text-center py-10 border rounded-xl bg-muted/20 text-muted-foreground">
                No players in roster. Add players on the dashboard roster page.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 w-full min-w-0">
                {sortedPlayerStats.map(playerStat => {
                  const goals = playerStat.goals || 0;
                  const assists = playerStat.assists || 0;
                  const hasStats = goals > 0 || assists > 0;

                  return (
                    <button
                      type="button"
                      key={`${game.id}-${playerStat.player_id}`}
                      onClick={() => setSelectedPlayerIdForModal(playerStat.player_id)}
                      className={`group relative flex flex-col items-center justify-center p-2 sm:p-2.5 rounded-lg sm:rounded-xl border text-center cursor-pointer transition-all duration-150 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 w-full min-w-0 overflow-hidden ${
                        hasStats 
                          ? 'bg-card border-primary/40 shadow-sm hover:border-primary hover:shadow-md hover:bg-accent/30' 
                          : 'bg-card/70 border-input hover:border-border hover:bg-card hover:shadow-sm'
                      }`}
                    >
                      {/* Player Name */}
                      <div 
                        className="font-bold text-xs sm:text-sm text-foreground truncate w-full min-w-0 px-0.5" 
                        title={playerStat.player?.name}
                      >
                        {playerStat.player?.name || 'Unknown Player'}
                      </div>

                      {/* Stats Badges */}
                      <div className="flex items-center justify-center gap-1 sm:gap-1.5 mt-1 sm:mt-1.5 flex-wrap w-full min-w-0">
                        <span className={`inline-flex items-center gap-0.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold shrink-0 ${
                          goals > 0 
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          <span className="text-[10px] sm:text-[11px]">⚽</span> {goals}
                        </span>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold shrink-0 ${
                          assists > 0 
                            ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          <span className="text-[10px] sm:text-[11px]">👟</span> {assists}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 3. Coach's Notes Section with Rich Formatting Toolbar */}
      <Card className="border shadow-md">
        <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Notebook className="w-5 h-5 text-primary" />
            <CardTitle className="text-xl">Coach's Notes</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {savedStatus && (
              <Badge variant="success" className="text-xs flex items-center gap-1 font-semibold">
                <Check className="w-3.5 h-3.5" />
                {savedStatus}
              </Badge>
            )}
            
            {/* View Mode Switcher: Edit / Preview */}
            <div className="flex items-center gap-1 bg-muted p-1 rounded-md border text-xs">
              <Button
                type="button"
                size="sm"
                variant={activeTab === 'edit' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('edit')}
                className="h-6 px-2 text-xs flex items-center gap-1"
              >
                <Edit3 className="w-3 h-3" />
                Write
              </Button>
              <Button
                type="button"
                size="sm"
                variant={activeTab === 'preview' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('preview')}
                className="h-6 px-2 text-xs flex items-center gap-1"
              >
                <Eye className="w-3 h-3" />
                Preview
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4 space-y-3">
          {activeTab === 'edit' ? (
            <>
              {/* Rich Formatting Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-1.5 bg-muted/40 rounded-lg border">
                <div className="flex items-center gap-1 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => insertFormatting('**', '**', 'bold text')}
                    className="h-8 px-2 text-xs font-bold"
                    title="Bold (**text**)"
                  >
                    <Bold className="w-4 h-4" />
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => insertFormatting('*', '*', 'italic text')}
                    className="h-8 px-2 text-xs italic"
                    title="Italic (*text*)"
                  >
                    <Italic className="w-4 h-4" />
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => insertFormatting('<u>', '</u>', 'underlined text')}
                    className="h-8 px-2 text-xs underline"
                    title="Underline (<u>text</u>)"
                  >
                    <Underline className="w-4 h-4" />
                  </Button>

                  <div className="h-4 w-[1px] bg-border mx-1" />

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => insertList('bullet')}
                    className="h-8 px-2 text-xs"
                    title="Bulleted List"
                  >
                    <List className="w-4 h-4" />
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => insertList('number')}
                    className="h-8 px-2 text-xs"
                    title="Numbered List"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Multiline Notes Textarea */}
              <Textarea
                ref={textareaRef}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter match tactical observations, player feedback, key moments, halftime notes..."
                rows={5}
                className="w-full text-sm leading-relaxed resize-y font-mono sm:font-sans"
              />
            </>
          ) : (
            /* Rendered Preview Tab */
            <div className="min-h-[140px] p-4 bg-muted/20 rounded-lg border space-y-1">
              {renderFormattedNotes(notes)}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearNotes}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Eraser className="w-4 h-4" />
              Clear
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleSaveNotes}
              className="flex items-center gap-1.5 font-semibold"
            >
              <Save className="w-4 h-4" />
              Save Notes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 4. Floating Stopwatch component fixed to bottom/center of screen */}
      <Stopwatch />

      {/* 5. Stat Recording Modal Dialog */}
      {activeModalPlayer && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={() => setSelectedPlayerIdForModal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="stat-modal-title"
        >
          <div
            className="bg-card text-card-foreground border shadow-2xl rounded-2xl max-w-sm w-full p-5 sm:p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-2 border-b pb-3.5">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary border border-primary/20 flex items-center justify-center font-extrabold text-base shrink-0 shadow-inner">
                  {getInitials(activeModalPlayer.player?.name)}
                </div>
                <div className="min-w-0">
                  <h3
                    id="stat-modal-title"
                    className="font-extrabold text-lg sm:text-xl truncate text-foreground"
                    title={activeModalPlayer.player?.name}
                  >
                    {activeModalPlayer.player?.name || 'Unknown Player'}
                  </h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                    <span>⚽ {activeModalPlayer.goals || 0} Goals</span>
                    <span>•</span>
                    <span>👟 {activeModalPlayer.assists || 0} Assists</span>
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground shrink-0 -mr-1.5 -mt-1.5"
                onClick={() => setSelectedPlayerIdForModal(null)}
                title="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Main Action Buttons */}
            <div className="space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
                Record Stat for Game
              </p>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="default"
                  size="lg"
                  className="h-24 flex-col gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md active:scale-95 transition-all text-base font-bold"
                  onClick={() => handleRecordStat(activeModalPlayer.player_id, 'goals')}
                >
                  <span className="text-3xl leading-none">⚽</span>
                  <span>+1 Goal</span>
                </Button>

                <Button
                  type="button"
                  variant="default"
                  size="lg"
                  className="h-24 flex-col gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-md active:scale-95 transition-all text-base font-bold"
                  onClick={() => handleRecordStat(activeModalPlayer.player_id, 'assists')}
                >
                  <span className="text-3xl leading-none">👟</span>
                  <span>+1 Assist</span>
                </Button>
              </div>
            </div>

            {/* Quick Mistake Correction / Decrement Section */}
            {((activeModalPlayer.goals || 0) > 0 || (activeModalPlayer.assists || 0) > 0) && (
              <div className="pt-2 border-t space-y-2">
                <p className="text-[11px] font-medium text-muted-foreground text-center">
                  Need to correct a mistake?
                </p>
                <div className="flex items-center justify-center gap-2">
                  {(activeModalPlayer.goals || 0) > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => handleSubtractStat(activeModalPlayer.player_id, 'goals')}
                    >
                      <Minus className="w-3 h-3 mr-1" /> -1 Goal
                    </Button>
                  )}
                  {(activeModalPlayer.assists || 0) > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => handleSubtractStat(activeModalPlayer.player_id, 'assists')}
                    >
                      <Minus className="w-3 h-3 mr-1" /> -1 Assist
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Cancel Button */}
            <div className="pt-1">
              <Button
                type="button"
                variant="outline"
                className="w-full text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedPlayerIdForModal(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GameStats;
